/*
 * pdf-coords.js — pure coordinate-transform math for /sign-pdf.
 *
 * THE ETALON CORE. No DOM, no globals, no side effects. Every function here
 * is a pure function so it can be locked by node unit tests
 * (tests/js/pdf-coords.test.mjs) and re-used in the browser.
 *
 * The problem it solves
 * ─────────────────────
 * PDF.js renders a page with a TOP-LEFT origin, in CSS pixels, at some scale.
 * pdf-lib writes content with a BOTTOM-LEFT origin, in PDF points (72/inch),
 * into the page's UNROTATED MediaBox — the PDF viewer then applies the page's
 * /Rotate (0|90|180|270) when displaying.
 *
 * The user drags a placement box over the *rendered* page. We store that box
 * as FRACTIONS of the rendered page — {nx,ny,nw,nh,rot} in [0,1], top-left
 * origin, `rot` = the box's own clockwise rotation in degrees. Fractions make
 * a placement independent of render scale / devicePixelRatio: the same numbers
 * re-project correctly at any zoom.
 *
 * placementToPdf() maps that fractional placement into pdf-lib drawImage args.
 *
 * Coordinate conventions
 * ──────────────────────
 *  - "rendered TL"  : origin top-left,    y grows DOWN  (what the user sees)
 *  - "rendered BL"  : origin bottom-left, y grows UP    (rendered, y-flipped)
 *  - "MediaBox"     : origin bottom-left, y grows UP    (what pdf-lib draws in)
 *  - pdf-lib drawImage `rotate` is COUNTER-CLOCKWISE-positive and pivots about
 *    the (x,y) anchor (the image's pre-rotation bottom-left corner).
 *  - CSS `transform: rotate()` (the placement box `rot`) is CLOCKWISE-positive.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfCoords = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    function norm360(d) { return ((d % 360) + 360) % 360; }

    /* Visible (rendered) page size in points, accounting for /Rotate. */
    function viewportSize(page) {
        var mediaW = page.mediaW, mediaH = page.mediaH;
        var pr = norm360(page.pdfRotate || 0);
        var swap = (pr === 90 || pr === 270);
        return { viewW: swap ? mediaH : mediaW, viewH: swap ? mediaW : mediaH, pdfRotate: pr };
    }

    /* Map a point from rendered-BL space into unrotated MediaBox space. */
    function renderedBLToMedia(x, y, mediaW, mediaH, pr) {
        if (pr === 90)  return { x: mediaW - y, y: x };
        if (pr === 180) return { x: mediaW - x, y: mediaH - y };
        if (pr === 270) return { x: y, y: mediaH - x };
        return { x: x, y: y };
    }

    /* Inverse of renderedBLToMedia — MediaBox point back to rendered-BL. */
    function mediaToRenderedBL(x, y, mediaW, mediaH, pr) {
        if (pr === 90)  return { x: y, y: mediaW - x };
        if (pr === 180) return { x: mediaW - x, y: mediaH - y };
        if (pr === 270) return { x: mediaH - y, y: x };
        return { x: x, y: y };
    }

    /*
     * placementToPdf(placement, pageInfo) -> { x, y, width, height, rotateDeg }
     *
     *   placement = { nx, ny, nw, nh, rot }   fractions of the rendered page,
     *               top-left origin; rot = clockwise degrees (default 0).
     *   pageInfo  = { mediaW, mediaH, pdfRotate }  unrotated MediaBox in points
     *               + the page's /Rotate.
     *
     * Returns args for pdf-lib `page.drawImage(img, { x, y, width, height,
     * rotate: degrees(rotateDeg) })`. (x,y) is the anchor that makes the image
     * land with the correct centre and orientation once the viewer applies
     * /Rotate. width/height are the image's own (pre-rotation) draw size.
     *
     * Method: compute the box CENTRE in MediaBox space and the TOTAL CCW
     * rotation, then derive the anchor — pdf-lib rotates about the anchor, so
     * anchor = centre − R_ccw(d)·(W/2, H/2). This is exact for any angle, which
     * is why it also handles the user's free `rot` and not just /Rotate.
     */
    function placementToPdf(placement, pageInfo) {
        var vs = viewportSize(pageInfo);
        var viewW = vs.viewW, viewH = vs.viewH, pr = vs.pdfRotate;
        var mediaW = pageInfo.mediaW, mediaH = pageInfo.mediaH;

        var boxW = placement.nw * viewW;
        var boxH = placement.nh * viewH;
        var boxLeft = placement.nx * viewW;
        var boxTop = placement.ny * viewH;
        var rot = placement.rot || 0;

        /* box centre: rendered TL -> rendered BL */
        var cxr = boxLeft + boxW / 2;
        var cyr = viewH - (boxTop + boxH / 2);
        var cm = renderedBLToMedia(cxr, cyr, mediaW, mediaH, pr);

        /* total CCW rotation in MediaBox: /Rotate inverse (+pr) minus the
           user's clockwise box rotation (−rot). */
        var d = pr - rot;
        var rad = d * Math.PI / 180;
        var cos = Math.cos(rad), sin = Math.sin(rad);
        var hw = boxW / 2, hh = boxH / 2;

        return {
            x: cm.x - (cos * hw - sin * hh),
            y: cm.y - (sin * hw + cos * hh),
            width: boxW,
            height: boxH,
            rotateDeg: norm360(d)
        };
    }

    /*
     * boxPoint(box, lx, ly) -> { x, y }
     *
     *   box    = what placementToPdf returned (anchor + rotateDeg).
     *   lx, ly = a point in the box's OWN frame: origin at its pre-rotation
     *            bottom-left corner, x along its width, y up its height.
     *
     * Returns that point in MediaBox space. Anything drawn INSIDE a box at an
     * offset — the baseline of its first line, the next word of a line, the
     * centre of an ellipse — must be carried through the box's rotation, or it
     * lands as if the box were upright. Until 2026-10-22 /pdf-editor added
     * those offsets along the page axes: a rotated text box, or ANY text on a
     * turned page, came out of the file word by word — every word turned
     * correctly about its own start, the words themselves stepping sideways
     * across the page (support chat 708: «криво вставляется текст»).
     */
    function boxPoint(box, lx, ly) {
        var rad = (box.rotateDeg || 0) * Math.PI / 180;
        var cos = Math.cos(rad), sin = Math.sin(rad);
        return { x: box.x + cos * lx - sin * ly, y: box.y + sin * lx + cos * ly };
    }

    /*
     * boxBounds(box) -> { x, y, width, height }
     * The upright MediaBox rectangle that covers the rotated box — what a PDF
     * annotation's /Rect is (a link, a widget): always axis-aligned in
     * unrotated page space, whatever the page /Rotate or the box's own turn.
     * `[x, y, x + width, y + height]` from the anchor is that rectangle only
     * when nothing is rotated.
     */
    function boxBounds(box) {
        var pts = [
            boxPoint(box, 0, 0), boxPoint(box, box.width, 0),
            boxPoint(box, box.width, box.height), boxPoint(box, 0, box.height)
        ];
        var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (var i = 0; i < pts.length; i++) {
            x0 = Math.min(x0, pts[i].x); x1 = Math.max(x1, pts[i].x);
            y0 = Math.min(y0, pts[i].y); y1 = Math.max(y1, pts[i].y);
        }
        return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    }

    /*
     * pdfToPlacement(drawArgs, pageInfo) -> { nx, ny, nw, nh, rot }
     * Inverse of placementToPdf. Used by the round-trip unit test; also handy
     * if a placement ever needs to be reconstructed from written PDF coords.
     */
    function pdfToPlacement(d, pageInfo) {
        var vs = viewportSize(pageInfo);
        var viewW = vs.viewW, viewH = vs.viewH, pr = vs.pdfRotate;
        var mediaW = pageInfo.mediaW, mediaH = pageInfo.mediaH;

        var rad = d.rotateDeg * Math.PI / 180;
        var cos = Math.cos(rad), sin = Math.sin(rad);
        var hw = d.width / 2, hh = d.height / 2;
        /* centre in MediaBox = anchor + R_ccw(d)·(W/2,H/2) */
        var cmx = d.x + (cos * hw - sin * hh);
        var cmy = d.y + (sin * hw + cos * hh);
        var cr = mediaToRenderedBL(cmx, cmy, mediaW, mediaH, pr);

        var boxW = d.width, boxH = d.height;
        var cxTL = cr.x;
        var cyTL = viewH - cr.y;
        return {
            nx: (cxTL - boxW / 2) / viewW,
            ny: (cyTL - boxH / 2) / viewH,
            nw: boxW / viewW,
            nh: boxH / viewH,
            rot: norm360(pr - d.rotateDeg)
        };
    }

    /*
     * fitImageBox(imgW, imgH, boxW, boxH) -> { width, height, dx, dy }
     * Aspect-preserving fit of an image into a box (letterboxed, centred).
     * Never stretches a signature when the user's box has a different ratio.
     */
    function fitImageBox(imgW, imgH, boxW, boxH) {
        if (imgW <= 0 || imgH <= 0 || boxW <= 0 || boxH <= 0) {
            return { width: 0, height: 0, dx: 0, dy: 0 };
        }
        var scale = Math.min(boxW / imgW, boxH / imgH);
        var w = imgW * scale, h = imgH * scale;
        return { width: w, height: h, dx: (boxW - w) / 2, dy: (boxH - h) / 2 };
    }

    /*
     * fitPlacement(placement, imgW, imgH) -> placement
     * Returns a new placement whose box is shrunk to the image's aspect ratio
     * while keeping the same centre. Feed the result to placementToPdf so the
     * written image is aspect-correct without any post-rotation offset math.
     */
    function fitPlacement(placement, imgW, imgH) {
        if (imgW <= 0 || imgH <= 0) return placement;
        var imgAspect = imgW / imgH;
        var boxAspect = placement.nw / placement.nh;
        var nw = placement.nw, nh = placement.nh;
        if (imgAspect > boxAspect) {
            nh = placement.nw / imgAspect;   // image wider than box — keep width, shrink height
        } else {
            nw = placement.nh * imgAspect;   // image taller than box — keep height, shrink width
        }
        return {
            pageIndex: placement.pageIndex,
            nx: placement.nx + (placement.nw - nw) / 2,
            ny: placement.ny + (placement.nh - nh) / 2,
            nw: nw,
            nh: nh,
            rot: placement.rot || 0
        };
    }

    /*
     * clampPlacement(placement) -> placement
     * Keeps the axis-aligned box fully inside the page [0,1]. A rotated box can
     * still poke a corner past the edge — this is a pragmatic guard, not a
     * geometric guarantee, so pdf-lib never receives wildly out-of-page coords.
     */
    function clampPlacement(p) {
        var nw = Math.min(Math.max(p.nw, 0.01), 1);
        var nh = Math.min(Math.max(p.nh, 0.01), 1);
        var nx = Math.min(Math.max(p.nx, 0), 1 - nw);
        var ny = Math.min(Math.max(p.ny, 0), 1 - nh);
        return {
            pageIndex: p.pageIndex,
            nx: nx, ny: ny, nw: nw, nh: nh,
            rot: norm360(p.rot || 0)
        };
    }

    return {
        placementToPdf: placementToPdf,
        boxPoint: boxPoint,
        boxBounds: boxBounds,
        pdfToPlacement: pdfToPlacement,
        fitImageBox: fitImageBox,
        fitPlacement: fitPlacement,
        clampPlacement: clampPlacement,
        viewportSize: viewportSize,
        _norm360: norm360
    };
});
